import { Link, useNavigate } from 'react-router-dom';
import { Heading, Text, Card, Badge, Button, type BadgeProps } from 'frosted-ui';
import { Plus16 } from '@frosted-ui/icons';
import type { Experiment, OfferClosedReason, Placement, TestOverview } from '../../types';
import { formatRelative } from '../../utils/format';
import {
  describeLegacyHistory,
  describeOffer,
  formatRpv,
  pluralize,
  variantLabel,
} from '../../utils/variants';
import { clearLeader, formatLift, liftVsControl, liveFor, readTest } from '../../utils/readout';
import { copyLinkLabel, linkName, spotForLabel, spotName } from '../../utils/paths';
import CopyButton from '../CopyButton';

interface TestCardProps {
  experiment: Experiment;
  // From GET /api/overview; undefined while loading and for archived tests.
  overview?: TestOverview;
}

const CLOSED_LABEL: Record<OfferClosedReason, string> = { sold_out: 'Sold out', expired: 'Expired' };

function statusBadge(exp: Experiment): { label: string; color: BadgeProps['color'] } {
  if (exp.archived) return { label: 'Archived', color: 'gray' };
  switch (exp.status) {
    case 'running':
      return { label: 'Live', color: 'green' };
    case 'stopped':
      return { label: 'Paused', color: 'yellow' };
    case 'winner_declared':
      return { label: 'Ended', color: 'blue' };
    default:
      return { label: 'Draft', color: 'gray' };
  }
}

export function PlacementChips({ placements }: { placements: Placement[] }) {
  if (placements.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
      {placements.map((p) => (
        <Badge key={p.id} size="1" variant="surface" color="gray" style={{ maxWidth: '100%' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{spotName(p)}</span>
        </Badge>
      ))}
    </div>
  );
}

/** One test on Home: placements, status, and revenue per visitor by variant. */
export default function TestCard({ experiment: exp, overview }: TestCardProps) {
  const navigate = useNavigate();
  // One count everywhere: live, paused and the test's own page.
  const visits = overview?.visitors ?? exp.stats?.visits ?? 0;
  const ended = exp.archived || exp.status === 'winner_declared';
  const data = {
    placements: overview?.placements ?? null,
    metrics: overview?.metrics ?? null,
  };

  // Past its stop date or sales limit. Ending or resuming the test doesn't reopen it.
  const offerClosed = exp.archived ? null : (overview?.offerClosed ?? null);
  const closed = exp.status === 'winner_declared' ? null : offerClosed;
  const badge = closed ? { label: CLOSED_LABEL[closed], color: 'orange' as const } : statusBadge(exp);
  const currency = exp.variants?.[0]?.currency ?? 'usd';
  const placements = data.placements ?? [];
  const linkPlacements = placements.filter((p) => p.link);
  const waiting = exp.status === 'running' && overview !== undefined && !overview.realTraffic && !closed;

  const live = liveFor(exp.startedAt, 'Live') ?? 'Live';
  const meta =
    exp.status === 'draft'
      ? `Draft · created ${formatRelative(exp.createdAt)}`
      : closed
        ? `${closed === 'sold_out' ? 'Sold out, so buyers can no longer get it' : 'Past its stop date, so buyers can no longer get it'} · ${pluralize(visits, 'visitor')}`
      : exp.status === 'running'
        ? waiting && visits === 0
          ? `${live} · waiting for first visitor`
          : `${live} · ${pluralize(visits, 'visitor')}`
        : `${badge.label} · ${pluralize(visits, 'visitor')}`;

  const header = (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 'var(--space-2) var(--space-3)',
      }}
    >
      <div style={{ minWidth: 0, flex: '1 1 220px' }}>
        <Heading size="4" style={{ overflowWrap: 'anywhere' }}>
          <Link
            to={`/experiments/${exp._id}`}
            // The whole card opens the test too: one press, one history entry.
            onClick={(e) => e.stopPropagation()}
            className="pl-tap"
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {exp.name}
          </Link>
        </Heading>
        <Text size="2" color="gray">
          {meta}
        </Text>
      </div>
      <Badge size="2" variant="soft" color={badge.color}>
        {badge.label}
      </Badge>
    </div>
  );

  // Ended: one line with the outcome.
  if (ended) {
    const winner = exp.variants?.find((v) => v._id === exp.winnerVariantId);
    const lift =
      winner && winner.name !== 'A' && data.metrics?.revenueComparable
        ? liftVsControl(data.metrics.variants, winner._id)
        : null;
    const where = offerClosed
      ? `offer closed: ${offerClosed === 'sold_out' ? 'sold out' : 'past its stop date'}`
      : placements.length > 0 && placements.every((p) => p.kind === 'in_whop')
        ? 'free members see the winner unless another test runs there'
        : 'links now sell the winner';
    return (
      <Card
        size="2"
        className="vara2-lift"
        style={{ cursor: 'pointer' }}
        onClick={() => navigate(`/experiments/${exp._id}`)}
      >
        {header}
        <Text size="2" color="gray" style={{ display: 'block', marginTop: 'var(--space-2)' }}>
          {winner
            ? [
                `Winner: ${variantLabel(winner.name)} · ${describeOffer(winner)}`,
                lift !== null ? `${formatLift(lift)} revenue per visitor` : null,
                exp.archived ? null : where,
              ]
                .filter(Boolean)
                .join(' · ')
            : exp.archived
              ? 'Archived · results kept'
              : 'Ended'}
        </Text>
      </Card>
    );
  }

  // Draft: not live, so nothing is tracked yet.
  if (exp.status === 'draft') {
    const imageMissing = exp.offerType === 'unlock' && !exp.unlock?.contentKey;
    return (
      <Card size="2">
        {header}
        <Text size="2" color="gray" style={{ display: 'block', margin: 'var(--space-3) 0' }}>
          {imageMissing
            ? 'Its image never finished uploading, so it can’t go live. Open it to delete it and start again.'
            : exp.offerType === 'unlock'
              ? 'Not live yet — its offer page shows the offer as unavailable and nothing is tracked.'
              : 'Not live yet — anyone who opens its links lands on your regular store and nothing is tracked.'}
        </Text>
        <Button size="2" variant="solid" onClick={() => navigate(`/experiments/new?id=${exp._id}`)}>
          {imageMissing ? 'Open draft' : 'Finish setup'}
        </Button>
      </Card>
    );
  }

  // Live but nobody real has opened it: teach, don't alarm.
  if (waiting) {
    const first = linkPlacements[0];
    const inWhop = placements.find((p) => p.kind === 'in_whop');
    const spot = first ? spotForLabel(first.label) : undefined;
    const yourLink = first && `Your ${linkName(spotName(first)).replace(/^Original/, 'original')}`;
    const where = !first
      ? 'Your test is live but no free member has opened the Upgrade tab yet.'
      : visits === 0
        ? `${yourLink} is live but nobody has opened it yet.`
        : `${yourLink} has ${pluralize(visits, 'visitor')} so far. That could be you: we can't tell a click on your own link from a buyer's.`;
    const nudge = spot
      ? ` ${spot.steps[1]}.`
      : first?.kind === 'partner'
        ? ` Did you send it to ${first.label}?`
        : first
          ? ' Did you swap it in where you post your current link?'
          : '';
    // A link alongside an in-whop spot doesn't make the Upgrade tab appear.
    const appNudge =
      inWhop && !inWhop.experienceId
        ? ` Also add Paywall Lab to ${inWhop.label}, or free members won't see the offer.`
        : '';
    return (
      <Card size="2" style={{ borderStyle: 'dashed' }}>
        {header}
        <div style={{ margin: 'var(--space-3) 0' }}>
          <PlacementChips placements={placements} />
        </div>
        <Text size="2" color="gray" style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
          {where}
          {nudge}
          {appNudge}
        </Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          {first?.link && <CopyButton text={first.link} label={copyLinkLabel(spotName(first))} />}
          <Button asChild size="2" variant="soft" color="gray">
            <Link to={`/experiments/${exp._id}/spots/new`}>
              <Plus16 />
              Add another spot
            </Link>
          </Button>
        </div>
      </Card>
    );
  }

  const readout = data.metrics ? readTest(data.metrics, Boolean(closed)) : null;
  const judgedByRevenue = data.metrics?.revenueComparable ?? true;
  const leader = data.metrics ? clearLeader(data.metrics.variants) : null;

  return (
    <Card
      size="2"
      className="vara2-lift"
      style={{ cursor: 'pointer' }}
      onClick={() => navigate(`/experiments/${exp._id}`)}
    >
      {header}
      <div style={{ margin: 'var(--space-3) 0' }}>
        <PlacementChips placements={placements} />
      </div>
      {visits === 0 ? (
        <Text size="2" color="gray">
          {data.metrics?.legacyHistory
            ? describeLegacyHistory(data.metrics.legacyHistory, currency)
            : `No visitors yet${exp.status === 'stopped' ? ' — resume it to start collecting.' : '.'}`}
        </Text>
      ) : !data.metrics ? (
        <Text size="2" color="gray">
          {pluralize(visits, 'visitor')} so far. Open it to see the numbers.
        </Text>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {/* One number first: the best price so far. */}
          {judgedByRevenue && leader && (
            <div>
              <Text size="6" weight="bold" className="vara2-tabular">
                {formatRpv(leader.revenuePerVisitor, currency)}
              </Text>{' '}
              <Text size="2" color="gray">
                per visitor, best so far: {variantLabel(leader.variantName)}
              </Text>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            {(exp.variants ?? []).map((v) => {
              const m = data.metrics?.variants.find((x) => x.variantId === v._id);
              return (
                <div
                  key={v._id}
                  style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '0 var(--space-3)' }}
                >
                  <Text size="2" color="gray" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    {variantLabel(v.name)} · {describeOffer(v)}
                  </Text>
                  <Text size="2" weight="medium" className="vara2-tabular">
                    {!m
                      ? '—'
                      : judgedByRevenue
                        ? m.visits === 0
                          ? 'No visitors yet'
                          : `${formatRpv(m.revenuePerVisitor, currency)} per visitor`
                        : `${m.starters} started`}
                  </Text>
                </div>
              );
            })}
          </div>
          {readout && (
            <Text size="2" color="gray">
              {readout.label}: {readout.text}
            </Text>
          )}
        </div>
      )}
    </Card>
  );
}
